import { PlayerRef } from '@remotion/player';
import { useCallback, type CSSProperties, type RefObject } from 'react';
import { GoToEndIcon } from '../icons/go-to-end';
import { GoToStartIcon } from '../icons/go-to-start';
import { timelineScrollContainerRef } from '../utils/restore-scroll-after-zoom';
import { useTimelineContext } from '../utils/use-context';
import {
  NextKeyframeFill,
  PreviousKeyframeFill,
  SkipNextFill,
  SkipPreviousFill,
} from '@crabs-ai/web-icons';
import { ToolButton } from '@crabs-ai/ds-react';
import { useTranslations } from 'next-intl';
import { EditorStarterItem } from '@/features/remotion-editor/editor/items/item-type';
import type { VideoItem } from '@/features/remotion-editor/editor/items/video/video-item-type';

const seekingIconStyle: CSSProperties = {
  width: 13,
};

export type PlayerRefProps = {
  playerRef: RefObject<PlayerRef | null>;
};

const isMainTrackVideoItem = (item: EditorStarterItem): item is VideoItem =>
  item.type === 'video' && !!item.isMainTrack;

export const GoToStartButton = ({ playerRef }: PlayerRefProps) => {
  const onClick = useCallback(() => {
    playerRef.current?.seekTo(0);
    timelineScrollContainerRef.current?.scrollTo({
      left: 0,
      behavior: 'instant',
    });
  }, [playerRef]);

  return (
    <button
      className="editor-starter-focus-ring p-2"
      onClick={onClick}
      type="button"
      aria-label="Go to Start"
    >
      <GoToStartIcon
        className="text-neutral-300"
        style={seekingIconStyle}
      />
    </button>
  );
};

export const GoToEndButton = ({ playerRef }: PlayerRefProps) => {
  const { durationInFrames } = useTimelineContext();

  const onClick = useCallback(() => {
    playerRef.current?.seekTo(durationInFrames);
    timelineScrollContainerRef.current?.scrollTo({
      left: durationInFrames,
      behavior: 'instant',
    });
  }, [durationInFrames, playerRef]);

  return (
    <button
      className="editor-starter-focus-ring p-2"
      onClick={onClick}
      type="button"
      aria-label="Go to End"
    >
      <GoToEndIcon style={seekingIconStyle} />
    </button>
  );
};

export const GoToPreviousFrameButton = ({ playerRef }: PlayerRefProps) => {
  const t = useTranslations();
  void playerRef; // playerRef 로직 구현 예정

  const goToPreviousFrame = () => {
    if (playerRef) {
      playerRef.current?.seekTo(playerRef.current.getCurrentFrame() - 1);
    }
  };

  return (
    <ToolButton
      state="default"
      onClick={goToPreviousFrame}
      isShowTooltip={true}
      tooltipNode={t('Editor.timeline-controls.previous-keyframe')}
      tooltipPosition="bottom"
      size="sm"
    >
      <PreviousKeyframeFill />
    </ToolButton>
  );
};

export const GoToPreviousClipButton = ({
  playerRef,
  items,
}: PlayerRefProps & { items: Record<string, EditorStarterItem> }) => {
  const t = useTranslations();
  const goToPreviousClip = useCallback(() => {
    const player = playerRef.current;
    if (!player) {
      return;
    }

    const currentFrame = player.getCurrentFrame();
    if (typeof currentFrame !== 'number') {
      return;
    }

    const sortedVideos = Object.values(items)
      .filter(isMainTrackVideoItem)
      .sort((a, b) => a.from - b.from);

    let previousClip: VideoItem | undefined;
    for (const item of sortedVideos) {
      if (item.from >= currentFrame) {
        break;
      }
      previousClip = item;
    }

    if (!previousClip) {
      return;
    }

    player.seekTo(previousClip.from);
    timelineScrollContainerRef.current?.scrollTo({
      left: previousClip.from,
      behavior: 'instant',
    });
  }, [items, playerRef]);

  return (
    <ToolButton
      state="default"
      onClick={goToPreviousClip}
      isShowTooltip={true}
      tooltipNode={t('Editor.timeline-controls.previous-clip')}
      tooltipPosition="bottom"
      size="sm"
    >
      <SkipPreviousFill />
    </ToolButton>
  );
};

export const GoToNextClipButton = ({
  playerRef,
  items,
}: PlayerRefProps & { items: Record<string, EditorStarterItem> }) => {
  const t = useTranslations();
  const goToNextClip = useCallback(() => {
    const player = playerRef.current;
    if (!player) {
      return;
    }

    const currentFrame = player.getCurrentFrame();

    const nextClip = Object.values(items)
      .filter(isMainTrackVideoItem)
      .sort((a, b) => a.from - b.from)
      .find((item) => item.from > currentFrame);

    if (!nextClip) {
      return;
    }

    player.seekTo(nextClip.from);
    timelineScrollContainerRef.current?.scrollTo({
      left: nextClip.from,
      behavior: 'instant',
    });
  }, [items, playerRef]);

  return (
    <ToolButton
      state="default"
      onClick={goToNextClip}
      isShowTooltip={true}
      tooltipNode={t('Editor.timeline-controls.next-clip')}
      tooltipPosition="bottom"
      size="sm"
    >
      <SkipNextFill />
    </ToolButton>
  );
};

export const GoToNextFrameButton = ({ playerRef }: PlayerRefProps) => {
  const t = useTranslations();

  const goToNextFrame = () => {
    if (playerRef) {
      playerRef.current?.seekTo(playerRef.current.getCurrentFrame() + 1);
    }
  };

  return (
    <ToolButton
      state="default"
      onClick={goToNextFrame}
      isShowTooltip={true}
      tooltipNode={t('Editor.timeline-controls.next-keyframe')}
      tooltipPosition="bottom"
      size="sm"
    >
      <NextKeyframeFill />
    </ToolButton>
  );
};
